/**
 * @file Membertoolbar is the toolbar displayed on public facing pages. It currently renders to the following React based routes:
 *
 * This component is rendered directly in the following paths:
 * - /signup/<signupListID>
 * - /give/<fundID>
 * - /give/
 * - /register
 * - /pledge/<campaignID>
 *
 * For details on where this appears in non-React UI's, see MemberToolbarPortal.jsx
 */
import { bool, func, number, string } from "prop-types";
import { useCallback, useEffect, useMemo, useRef } from "react";

import styled from "styled-components";

import {
  useCanContactUs,
  useHasContactUs,
  useIsLoggedIn,
} from "Flocknote/stateHooks";

import { AnimatePresence, motion } from "framer-motion";

import FnrSlides from "UI/FnrSlides";
import { colors } from "UI/colors";

import { addOpacity, ensureLeadingHex } from "Utilities/Colors/colorUtils";
import useOnClickOutside from "Utilities/Hooks/useOnClickOutside";
import { useWindowSize } from "Utilities/Hooks/useWindowSize";
import { deepShadow } from "Utilities/interpolations/shadow";

import {
  MemberToolbarState,
  useMemberToobarContext,
} from "./MemberToolbarState";
import { ActiveContent } from "./components/ActiveContent/ActiveContent";
import ContactButton from "./components/ContactButton";
import { HelpLink } from "./components/HelpLink";
import HomeButton from "./components/HomeButton";
import UserAndLogin from "./components/UserAndLogin/UserAndLogin";
import { SetLanguage } from "./components/shared/SetLanguage";
import { contentKeys } from "./contentKeys";
import {
  useEscapeAction,
  useHasActiveContent,
  useShowMemberToolbar,
} from "./stateHooks";
import {
  ACTIVE_WIDTH,
  INACTIVE_HEIGHT,
  INACTIVE_WIDTH,
  INACTIVE_WIDTH_LOGIN_ONLY,
  LOGIN_WIDTH,
  TOOLBAR_MARGIN,
  TOOLBAR_PADDING_X,
  TOOL_BAR_TO_ACTIVE_CONTENT_SPACE,
} from "./styleConstants";

const rightContentConfig = [
  { value: HelpLink, key: "helpLink" },
  { value: SetLanguage, key: "setLanguage" },
  { value: ContactButton, key: "contactButton" },
  { value: () => null, key: "nothing" },
];

const NavButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: ${INACTIVE_HEIGHT}px;
`;

const MemberToolbarContents = (props) => {
  const wrapperRef = useRef();
  const {
    setActiveContentKey,
    activeContentKey,
    isLoggingOut,
    setIsLoggingOut,
    isDark,
    clickAwayDisabled,
    logoutGoesToLogin,
    languageForLoggedOut,
    bottomOffset,
  } = useMemberToobarContext();
  const { width: windowWidth } = useWindowSize();
  const [hasContactUs] = useHasContactUs();
  const [canContactUs] = useCanContactUs();

  const onClickOutside = useCallback(() => {
    if (!clickAwayDisabled) setActiveContentKey(contentKeys.nothing);
  }, [clickAwayDisabled, setActiveContentKey]);

  useOnClickOutside(wrapperRef, onClickOutside, "memberToolbarCancelKey");

  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    if (!isLoggedIn && isLoggingOut && !logoutGoesToLogin)
      setIsLoggingOut(false);
  }, [isLoggedIn, isLoggingOut, setIsLoggingOut, logoutGoesToLogin]);

  const hasActiveContent = useHasActiveContent();

  const rightContentActiveKey = useMemo(() => {
    if (!isLoggedIn && !languageForLoggedOut) return "nothing";
    if (!isLoggedIn) return "setLanguage";
    if (!hasContactUs) return "helpLink";
    if (hasContactUs) return "contactButton";
  }, [isLoggedIn, hasContactUs, languageForLoggedOut]);

  const escapeAction = useEscapeAction();

  const width = useMemo(() => {
    const loginActive = activeContentKey === "login";

    const maxWidth = windowWidth - TOOLBAR_PADDING_X * 2 - TOOLBAR_MARGIN * 2;
    if (loginActive) return Math.min(LOGIN_WIDTH, maxWidth);
    if (hasActiveContent) return Math.min(ACTIVE_WIDTH, maxWidth);
    if (!isLoggedIn && !languageForLoggedOut)
      return Math.min(INACTIVE_WIDTH_LOGIN_ONLY, maxWidth);
    return Math.min(INACTIVE_WIDTH, maxWidth);
  }, [
    activeContentKey,
    windowWidth,
    hasActiveContent,
    isLoggedIn,
    languageForLoggedOut,
  ]);

  const backgroundImage = useMemo(() => {
    let color1;
    let color2;
    if (isDark && hasActiveContent) {
      color1 = "rgb(24, 41, 65)";
      color2 = "rgb(24, 41, 65)";
    } else if (isDark) {
      color1 = "rgb(24, 41, 65, 0.55)";
      color2 = "rgb(24, 41, 65, 0.65)";
    } else if (hasActiveContent) {
      color1 = "rgb(242, 242, 241)";
      color2 = "rgb(242, 242, 241)";
    } else {
      color1 = "rgb(255, 255, 255, 0.55)";
      color2 = "rgb(255, 255, 255, 0.65)";
    }
    return `linear-gradient(to right, ${color1}, ${color2})`;
  }, [hasActiveContent, isDark]);

  const show = useShowMemberToolbar();
  if (!canContactUs) return null;

  return (
    <AnimatePresence>
      {show && (
        <MemberToolbarWrapper
          data-cy="MemberToolbarWrapper"
          tabIndex={-1}
          onKeyDown={({ code }) => code === "Escape" && escapeAction()}
          key="MemberToolbarWrapper"
          ref={wrapperRef}
          $hasActiveContent={hasActiveContent}
          $isDark={isDark}
          $bottomOffset={bottomOffset}
          initial={{
            transform: "translateY(100%) translateX(-50%)",
            width,
            opacity: 0,
          }}
          animate={{
            transform: "translateY(0%) translateX(-50%)",
            opacity: 1,
            width,
            backgroundImage,
          }}
          exit={{
            transform: "translateY(100%) translateX(-50%)",
            opacity: 0,
          }}
          transition={{
            default: { duration: 0.15 },
            backgroundImage: { duration: 0.3 },
            width: {
              type: hasActiveContent ? "spring" : "tween",
              damping: 13,
              mass: 0.65,
              stiffness: 160,
            },
          }}
          {...props}
        >
          <ActiveContentWrapper
            $addMarginBottom={activeContentKey !== contentKeys.nothing}
          >
            <ActiveContent />
          </ActiveContentWrapper>
          <ButtonWrapper>
            <PrimaryButtonsContainer>
              <NavButtonWrapper>
                <HomeButton show={isLoggedIn} />
                <UserAndLogin />
              </NavButtonWrapper>
            </PrimaryButtonsContainer>
            <FnrSlides
              clip={false}
              variant="subtle"
              components={rightContentConfig}
              activeKey={rightContentActiveKey}
              fullWidth
            />
          </ButtonWrapper>
        </MemberToolbarWrapper>
      )}
    </AnimatePresence>
  );
};

/**
 *
 * @param {object} param0
 * @param {function} param0.onAuthChange - fire a function that should happen when the login level changes.
 * @param {LoginVariant} param0.loginVariant
 * @param {string} param0.tabID - a uuid to be passed with each call to the login endpoint
 * @param {string} param0.accentColor
 * @param {boolean} param0.logoutGoesToLogin - should the user be redirected to the login screen after logout?
 * @param {boolean} param0.isDark - defaults to true. Set to false for a light theme.
 * @param {string} param0.contextInquiryText - text that displays on the button for message to be sent to a specific admin.
 * @param {boolean} param0.languageForLoggedOut - Adds the language picker for logged out users.
 * @returns
 */
const MemberToolbar = ({
  onAuthChange,
  loginVariant,
  tabID,
  accentColor = "#2867ac",
  logoutGoesToLogin,
  isDark = true,
  contextInquiryText,
  languageForLoggedOut,
  bottomOffset,
  ...rest
}) => (
  <MemberToolbarState
    onAuthChange={onAuthChange}
    loginVariant={loginVariant}
    tabID={tabID}
    accentColor={ensureLeadingHex(accentColor)}
    logoutGoesToLogin={logoutGoesToLogin}
    isDark={isDark}
    contextInquiryText={contextInquiryText}
    languageForLoggedOut={languageForLoggedOut}
    bottomOffset={bottomOffset}
  >
    <MemberToolbarContents {...rest} />
  </MemberToolbarState>
);

MemberToolbar.propTypes = {
  onAuthChange: func,
  loginVariant: string,
  tabID: string,
  accentColor: string,
  logoutGoesToLogin: bool,
  isDark: bool,
  contextInquiryText: string,
  languageForLoggedOut: bool,
  bottomOffset: number,
};

const MemberToolbarWrapper = styled(motion.div)`
  backdrop-filter: blur(6px);
  position: fixed;
  ${deepShadow}
  bottom: ${({ $bottomOffset }) => TOOLBAR_MARGIN + ($bottomOffset || 0)}px;
  left: 50vw;
  padding: 8px 0px;
  display: flex;
  flex-direction: column;
  border: solid 1px ${addOpacity(colors.white, 0.3)};
  border-radius: 26px;
  z-index: 4001;
  transition: padding 300ms ease-in-out, bottom 300ms ease-in-out;
`;

const ActiveContentWrapper = styled.div`
  margin-bottom: ${({ $addMarginBottom }) =>
    $addMarginBottom ? TOOL_BAR_TO_ACTIVE_CONTENT_SPACE : 0}px;
  transition: margin-bottom 300ms ease-in-out;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 0px ${TOOLBAR_PADDING_X}px;
`;

const PrimaryButtonsContainer = styled.div`
  flex-grow: 1;
`;

export { MemberToolbar };
